import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, PieChart, RefreshCw } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;

// "Gráficos" — always-visible pair of charts for the Gastos section. Powered
// by QuickChart URLs generated on the backend so we don't have to bundle a
// charting library. Refreshes when the parent period changes and via a manual
// refresh button.
export default function ExpenseChartsSection({ period }) {
  const { theme } = useTheme();
  const [byCategoryUrl, setByCategoryUrl] = useState(null);
  const [byMonthUrl, setByMonthUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, month] = await Promise.all([
        axios.get(`${API}/api/expenses/chart-url?tipo=por_categoria&theme=${theme}`, { withCredentials: true }),
        axios.get(`${API}/api/expenses/chart-url?tipo=por_mes&theme=${theme}`, { withCredentials: true }),
      ]);
      setByCategoryUrl(cat.data?.chart_url || null);
      setByMonthUrl(month.data?.chart_url || null);
    } catch {
      setByCategoryUrl(null);
      setByMonthUrl(null);
    } finally {
      setLoading(false);
    }
  }, [theme]);

  useEffect(() => { load(); }, [load, period]);

  const refresh = () => {
    setCacheBust((n) => n + 1);
    load();
  };

  const bust = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}_=${cacheBust}` : null);

  return (
    <div
      data-testid="expenses-charts-section"
      className="bg-surface rounded-3xl shadow-lg shadow-foreground/5 dark:shadow-none dark:ring-1 dark:ring-white/5 p-6 sm:p-8"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary/15 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-primary" strokeWidth={1.6} />
          </div>
          <div>
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-primary">
              Gráficos
            </p>
            <h2 className="text-xl sm:text-2xl font-display font-bold tracking-tight text-foreground">
              Cómo se ven tus gastos
            </h2>
          </div>
        </div>
        <button
          data-testid="expenses-charts-refresh-btn"
          onClick={refresh}
          className="p-2.5 hover:bg-surface-2 rounded-full text-foreground transition-colors duration-200"
          aria-label="Actualizar gráficos"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.6} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {/* Torta por categoría — mes actual */}
        <div
          data-testid="chart-by-category"
          className="rounded-3xl bg-surface-2 p-5 ring-1 ring-border"
        >
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-4 h-4 text-primary" strokeWidth={1.6} />
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Por categoría (mes actual)
            </p>
          </div>
          {byCategoryUrl ? (
            <img
              src={bust(byCategoryUrl)}
              alt="Gráfico de gastos por categoría"
              data-testid="chart-by-category-img"
              className="w-full h-auto rounded-2xl"
              loading="lazy"
            />
          ) : (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {loading ? 'Generando gráfico…' : 'Todavía no hay gastos este mes para armar el gráfico.'}
            </div>
          )}
        </div>

        {/* Barras por mes — últimos 6 meses */}
        <div
          data-testid="chart-by-month"
          className="rounded-3xl bg-surface-2 p-5 ring-1 ring-border"
        >
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-primary" strokeWidth={1.6} />
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Últimos 6 meses
            </p>
          </div>
          {byMonthUrl ? (
            <img
              src={bust(byMonthUrl)}
              alt="Gráfico de gastos por mes"
              data-testid="chart-by-month-img"
              className="w-full h-auto rounded-2xl"
              loading="lazy"
            />
          ) : (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {loading ? 'Generando gráfico…' : 'Sin datos suficientes para mostrar la tendencia.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
